import { cn } from '@/lib/cn';

/**
 * Product mark.
 *
 * Inline SVG (no network request, no flash) so the shell paints instantly in
 * the Teams iframe. Collapses to the monogram tile when the rail is narrow.
 */

export interface BrandProps {
  /** Hides the wordmark, leaving only the tile. */
  compact?: boolean;
  className?: string;
}

export function BrandMark({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 1080 1080"
      className={cn('h-8 w-8 shrink-0', className)}
      role="img"
      aria-label="ADCO"
      focusable="false"
    >
      {/* The tile lifts a step in dark mode: the logo navy is so close to the
          dark surface that the mark would float with no edge. */}
      <rect width="1080" height="1080" rx="180" className="fill-brand-950 dark:fill-brand-800" />
      {/* ADCO isotype, centred on its own geometry and enlarged to fill the
          tile. The artwork's bounding box is 619 x 710 units centred on
          (532.6, 553.6) — not on (540, 540) — so scaling about the tile centre
          left it small, low and off to one side. At 32px in the rail that read
          as a mark cut off by its own container rather than as a whole shape.
          The isotype is six separate facets with hairline seams between them;
          below roughly 24px those seams stop resolving, which is the other half
          of why it looked broken. Filling the tile buys back the pixels the
          seams need. */}
      <g transform="translate(540 540) scale(1.07) translate(-532.63 -553.62)">
        <polygon fill="rgb(var(--color-gold-500))" points="709.51 464.78 840.83 694.16 841.92 389 709.51 464.78" />
        <polygon fill="#ffffff" points="224.5 386.78 223.33 715.79 508.84 552.29 224.5 386.78" />
        <polygon fill="rgb(var(--color-gold-500))" points="566.31 214.81 697.71 444.18 830.12 368.35 566.31 214.81" />
        <polygon fill="#ffffff" points="236.46 366.26 532.67 538.64 677.06 456 529.5 198.4 236.46 366.26" />
        <polygon fill="#ffffff" points="235.13 736.43 531.34 908.84 828.77 738.57 532.58 566.13 235.13 736.43" />
        <polygon fill="#ffffff" points="556.42 552.46 820.15 705.98 688.85 476.58 556.42 552.46" />
      </g>
    </svg>
  );
}

export function Brand({ compact = false, className }: BrandProps): JSX.Element {
  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <BrandMark />
      {compact ? null : (
        <span className="flex min-w-0 flex-col leading-none">
          <span className="truncate font-display text-body font-bold tracking-tight text-ink">
            Salas de juntas
          </span>
          <span className="truncate text-caption font-medium text-ink-muted">ADCO</span>
        </span>
      )}
    </span>
  );
}
